import React from 'react';

import * as routes from "serviceCatalog/routes";

import { SFConnection, SFEntityID } from 'utils/types';
import css from './ConnectionProfile.scss';
import ConnectionHeader from './ConnectionHeader';
import HoursSection from './HoursSection';
import ContactSection from './ContactSection';
import ServicesSection from './ServicesSection';
import AboutSection from './AboutSection';
import LinksSection from './LinksSection';
import StyledLink from '../../common/StyledLink';
import { defaultScheduleLinkForConnection } from 'utils/scheduler';
import { getTenantConfig } from 'utils/tenant';
import { isEmpty } from 'lodash';

/**
 * A university staff member (the staff member being a student's "Connection") profile,
 * as presented to a student in their Success Network.
 */
export default class ConnectionProfile extends React.Component {
  isValid(prop) {
    return !(prop === null || prop === "" || typeof prop === "undefined");
  }

  get appointmentSchedulingLink() {
    if(!this.props.onlineSchedulingAvailable) {
      return null;
    }

    return getTenantConfig('MOBILE_APPOINTMENTS_ENABLED') ?
      <StyledLink
        text="Schedule Appointment"
        to={
          routes.toScheduleStaffUserAppointmentForService({
            staffUserId: this.props.personId,
            serviceId: this.props.serviceId
          })
        } />
      : <a href={defaultScheduleLinkForConnection(this.props.personId)}>Schedule</a>;
  }

  get officeHoursSection() {
    const { officeHours, personId } = this.props;
    return isEmpty(officeHours)
      ? null : <HoursSection personId={personId} officeHours={officeHours} />;
  }

  get contactSection() {
    const { phoneNumber, email, mobilePhone, videoPhone } = this.props;
    return this.isValid(phoneNumber) || this.isValid(email)
      ? <ContactSection phoneNumber={phoneNumber} email={email} mobilePhone={mobilePhone} videoPhone={videoPhone}/>
      : null;
  }

  get servicesSection() {
    const { services, isStudent } = this.props;
    return services && services.length > 0 ? <ServicesSection services={services} isStudent={isStudent} /> : null;
  }

  get linksSection() {
    const hasAtLeastOneLinkAvailable = this.props.appointmentLinkAvailable || this.props.profileLinkAvailable;

    const showLinksSection = (
      getTenantConfig('DEEP_LINKS_ENABLED') // Deep links must be enabled
      && !!getTenantConfig('DEEP_LINK_SSO_METHOD') // Needs to have a SSO method set
      && this.props.isStaff // Only staff users can see links section
      && hasAtLeastOneLinkAvailable // Needs at least one link available
    );

    return showLinksSection
      ? (
        <LinksSection
          personId={this.props.personId}
          appointmentLinkAvailable={this.props.appointmentLinkAvailable}
          profileLinkAvailable={this.props.profileLinkAvailable}
        />
      )
      : null;
  }

  get aboutSection() {
    const { biography, overview, fullName } = this.props;
    return this.isValid(biography) || this.isValid(overview)
      ? <AboutSection biography={biography} overview={overview} fullName={fullName} />
      : null;
  }

  render() {
    const { personId, uuid, fullName, roles, title, photoAvailable} = this.props;
    return (
      <div>
        <ConnectionHeader fullName={fullName} roles={roles} personId={personId} uuid={uuid} photoAvailable={photoAvailable} title={title} />
        <div className={css['connection-container']}>
          <div className={css['left-side']}>
            {this.appointmentSchedulingLink}
            {this.contactSection}
            {this.servicesSection}
            {this.linksSection}
          </div>
          <div className={css['right-side']}>
            {this.officeHoursSection}
            {this.aboutSection}
          </div>
        </div>
      </div>
    );
  }
}

ConnectionProfile.propTypes = {
  ...SFConnection,

  /**
   * An optional university service ID, set if a student is viewing a staff
   * member (connection) who's a member of a university service.
   */
  serviceId: SFEntityID
};
